import { useEffect, useRef, useState } from 'react';

import {

  ArrowRight, BarChart3, Check, ChefHat, Languages, Palette, QrCode, RefreshCw,

  ScanLine, ShoppingBag, Sparkles, Store, UtensilsCrossed, Zap, ClipboardCheck,
  Smartphone, ClipboardList, BellRing, ListChecks,

} from 'lucide-react';

import { QRCodeSVG } from 'qrcode.react';

import { Link } from 'react-router-dom';

import { useLanguage } from '../../contexts/LanguageContext';

import '../../styles/Landing/landing.css';
const FEATURE_ICONS = {

  digitalMenu: QrCode, ordering: ShoppingBag, aiImport: Sparkles,

  multilingual: Languages, analytics: BarChart3, customization: Palette,

};

const BUSINESS_ICONS = {

  restaurant: UtensilsCrossed, cafe: Store, fastFood: ChefHat,

  bakery: ShoppingBag, hotel: Store, darkKitchen: Zap,

};

const DEMO_MENU_URL = 'https://app.lmenu.net/devforge/menus';

const CHART_BARS = [48, 68, 55, 82, 64, 92, 74];

const PREVIEW_CATEGORIES = ['category1', 'category2', 'category3'];

const PREVIEW_PRODUCTS = [['01', 'product1', 89], ['02', 'product2', 72], ['03', 'product3', 54]];

// Demo-only content for the language switcher: [code, label, dir, categories, dishes, cart label]

const DEMO_LANGS = [

  ['en', 'English', 'ltr', ['Mains', 'Drinks', 'Desserts'], ['Lamb tagine', 'Mint tea', 'Msemen'], '2 items'],

  ['fr', 'Français', 'ltr', ['Plats', 'Boissons', 'Desserts'], ['Tajine d’agneau', 'Thé à la menthe', 'Msemen'], '2 articles'],

  ['es', 'Español', 'ltr', ['Platos', 'Bebidas', 'Postres'], ['Tajín de cordero', 'Té de menta', 'Msemen'], '2 artículos'],

  ['de', 'Deutsch', 'ltr', ['Hauptgerichte', 'Getränke', 'Desserts'], ['Lammtajine', 'Minztee', 'Msemen'], '2 Artikel'],

  ['zh', '中文', 'ltr', ['主菜', '饮品', '甜点'], ['羊肉塔吉锅', '薄荷茶', '摩洛哥薄饼'], '2 件商品'],

  ['ru', 'Русский', 'ltr', ['Основные блюда', 'Напитки', 'Десерты'], ['Тажин с бараниной', 'Мятный чай', 'Мсемен'], '2 товара'],

  ['it', 'Italiano', 'ltr', ['Piatti principali', 'Bevande', 'Dolci'], ['Tajine di agnello', 'Tè alla menta', 'Msemen'], '2 articoli'],

  ['pt', 'Português', 'ltr', ['Pratos principais', 'Bebidas', 'Sobremesas'], ['Tajine de borrego', 'Chá de hortelã', 'Msemen'], '2 itens'],

  ['tr', 'Türkçe', 'ltr', ['Ana yemekler', 'İçecekler', 'Tatlılar'], ['Kuzu tajin', 'Nane çayı', 'Msemen'], '2 ürün'],

  ['nl', 'Nederlands', 'ltr', ['Hoofdgerechten', 'Dranken', 'Desserts'], ['Lamstajine', 'Muntthee', 'Msemen'], '2 items'],

  ['ja', '日本語', 'ltr', ['メイン料理', 'ドリンク', 'デザート'], ['ラム肉のタジン', 'ミントティー', 'ムサンメン'], '2点'],

  ['ar', 'العربية', 'rtl', ['الأطباق', 'المشروبات', 'الحلويات'], ['طاجين لحم', 'شاي بالنعناع', 'مسمن'], 'عنصران'],

];

function useReveal() {

  useEffect(() => {

    const els = document.querySelectorAll('[data-reveal]');

    if (!('IntersectionObserver' in window)) {

      els.forEach((el) => el.classList.add('is-visible'));

      return undefined;

    }

    const io = new IntersectionObserver(

      (entries) => entries.forEach((e) => {

        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }

      }),

      { threshold: 0.18 },

    );

    els.forEach((el) => io.observe(el));

    return () => io.disconnect();

  }, []);

}

export default function LandingPage() {

  const { t, isRtl } = useLanguage();

  useReveal();

  const [activeCat, setActiveCat] = useState(0);

  const [langIndex, setLangIndex] = useState(0);

  const [priceStep, setPriceStep] = useState(0);

  const [pulse, setPulse] = useState(false);

  const pulseTimer = useRef();

  const lang = DEMO_LANGS[langIndex];

  const price = 89 + priceStep * 6;

  // Hero phone: gently cycle categories so the preview feels alive (paused for reduced motion)

  useEffect(() => {

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const id = setInterval(() => setActiveCat((c) => (c + 1) % PREVIEW_CATEGORIES.length), 3600);

    return () => clearInterval(id);

  }, []);

  useEffect(() => () => clearTimeout(pulseTimer.current), []);

  const updatePrice = () => {

    setPriceStep((s) => (s + 1) % 4);

    setPulse(true);

    clearTimeout(pulseTimer.current);

    pulseTimer.current = setTimeout(() => setPulse(false), 500);

  };

  return (

    <div className={`landing-page ${isRtl ? 'is-rtl' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>

      <main id="top">

        {/* HERO */}

        <section className="landing-hero">

          <div className="landing-container landing-hero__grid">

            <div className="landing-hero__content">

              <h1>

                {t('landing.hero.titleStart')} <span>{t('landing.hero.titleHighlight')}</span> {t('landing.hero.titleEnd')}

              </h1>

              <p className="landing-hero__description">{t('landing.hero.description')}</p>

              <div className="landing-hero__actions">

                <Link to="/free-trial" className="landing-button">

                  {t('landing.freeTrial.cta')}<ArrowRight size={18} />

                </Link>

              </div>

              <div className="landing-hero__proof">

                {['noApp', 'fastSetup', 'multilingual'].map((key) => (

                  <span key={key}><Check size={15} />{t(`landing.hero.proof.${key}`)}</span>

                ))}

              </div>

            </div>

            <div className="landing-hero__visual" aria-hidden="true">

              <div className="landing-hero__glow landing-hero__glow--one" />

              <div className="landing-hero__glow landing-hero__glow--two" />

              <div className="landing-dashboard-card">

                <div className="landing-dashboard-card__toolbar">

                  <span /><span /><span /><small>{t('landing.product.canvasTitle')}</small>

                </div>

                <div className="landing-dashboard-card__head">

                  <div>

                    <small>{t('landing.preview.dashboardEyebrow')}</small>

                    <strong>{t('landing.preview.dashboardTitle')}</strong>

                  </div>

                  <span><BarChart3 size={18} /></span>

                </div>

                <div className="landing-dashboard-stats">

                  <article><span>{t('landing.preview.orders')}</span><strong>128</strong><small>+18%</small></article>

                  <article><span>{t('landing.preview.views')}</span><strong>2.4K</strong><small>+31%</small></article>

                  <article><span>{t('landing.preview.products')}</span><strong>64</strong><small>{t('landing.preview.live')}</small></article>

                </div>

                <div className="landing-dashboard-chart">

                  {CHART_BARS.map((h, i) => (

                    <span key={i} className="landing-dashboard-chart__bar" style={{ '--h': `${h}%`, '--i': i }} />

                  ))}

                </div>

                <div className="landing-dashboard-image">

                  <div><ChefHat size={20} /><strong>{t('landing.preview.restaurantName')}</strong></div>

                </div>

              </div>

              <div className="landing-phone">

                <div className="landing-phone__speaker" />

                <div className="landing-phone__screen">

                  <div className="landing-scan-overlay"><ScanLine size={34} /></div>

                  <div className="landing-menu-preview__hero">

                    <span><UtensilsCrossed size={18} /></span>

                    <div>

                      <small>{t('landing.preview.restaurantEyebrow')}</small>

                      <strong>{t('landing.preview.restaurantName')}</strong>

                      <p>{t('landing.preview.restaurantSubtitle')}</p>

                    </div>

                  </div>

                  <div className="landing-menu-preview__search"><ScanLine size={14} />{t('landing.preview.search')}</div>

                  <div className="landing-menu-preview__categories">

                    {PREVIEW_CATEGORIES.map((key, i) => (

                      <span key={key} className={i === activeCat ? 'is-active' : ''}>{t(`landing.preview.${key}`)}</span>

                    ))}

                  </div>

                  <div className="landing-menu-preview__products" key={activeCat}>

                    {PREVIEW_PRODUCTS.map(([n, key, p]) => (

                      <article key={key}>

                        <span className="landing-menu-preview__food">{n}</span>

                        <div>

                          <strong>{t(`landing.preview.${key}`)}</strong>

                          <small>{t('landing.preview.productDescription')}</small>

                          <b>{p} MAD</b>

                        </div>

                      </article>

                    ))}

                  </div>

                </div>

              </div>

              <div className="landing-floating-card landing-floating-card--qr">

                <span><QrCode size={20} /></span>

                <div><strong>{t('landing.preview.qrTitle')}</strong><small>{t('landing.preview.qrSubtitle')}</small></div>

              </div>

              <div className="landing-floating-card landing-floating-card--order">

                <span><ShoppingBag size={20} /></span>

                <div><strong>{t('landing.preview.orderTitle')}</strong><small>{t('landing.preview.orderSubtitle')}</small></div>

              </div>

            </div>

          </div>

        </section>

        <section className="landing-trust-strip">

          <div className="landing-container landing-trust-strip__inner">

            <p>{t('landing.trust.title')}</p>

            <div>

              {['restaurants', 'cafes', 'hotels', 'bakeries', 'darkKitchens'].map((key) => (

                <span key={key}>{t(`landing.trust.${key}`)}</span>

              ))}

            </div>

          </div>

        </section>

        {/* FEATURES: bento (sizes defined in landing-improvements.css) */}

        <section id="features" className="landing-section">

          <div className="landing-container">

            <div className="landing-section-heading" data-reveal>

              <span>{t('landing.features.eyebrow')}</span>

              <h2>{t('landing.features.title')}</h2>

              <p>{t('landing.features.description')}</p>

            </div>

            <div className="landing-feature-grid landing-feature-grid--bento">

              {Object.entries(FEATURE_ICONS).map(([key, Icon], i) => (

                <article key={key} className={`landing-feature-card landing-feature-card--${key}`} data-reveal style={{ '--d': `${i * 60}ms` }}>

                  <span className="landing-feature-card__icon"><Icon size={22} /></span>

                  <h3>{t(`landing.features.items.${key}.title`)}</h3>

                  <p>{t(`landing.features.items.${key}.description`)}</p>

                </article>

              ))}

            </div>

          </div>

        </section>

        {/* PRODUCT (unchanged structure, keeps your QR + phone composition) */}

        <section id="product-preview" className="landing-section landing-product">

          <div className="landing-container landing-product__grid">

            <div className="landing-product__media" aria-hidden="true" data-reveal>

              <div className="landing-qr-preview">

                <div className="landing-qr-preview__header">

                  <span className="landing-qr-preview__header-icon"><QrCode size={17} /></span>

                  <div><strong>{t('landing.preview.qrTitle')}</strong><small>{t('landing.preview.qrSubtitle')}</small></div>

                </div>

                <div className="landing-qr-preview__stage">

                  <div className="landing-table-card">

                    <div className="landing-table-card__brand">

                      <span><UtensilsCrossed size={15} /></span><strong>{t('landing.preview.restaurantName')}</strong>

                    </div>

                    <div className="landing-table-card__qr">

                      <QRCodeSVG value={DEMO_MENU_URL} size={122} bgColor="#ffffff" fgColor="#1f2937" level="M" />

                    </div>

                    <small>{t('landing.preview.qrSubtitle')}</small>

                  </div>

                  <div className="landing-qr-phone">

                    <div className="landing-qr-phone__top">

                      <span><ChefHat size={14} /></span>

                      <div><strong>{t('landing.preview.restaurantName')}</strong><small>{t('landing.preview.restaurantSubtitle')}</small></div>

                    </div>

                    <div className="landing-qr-phone__tabs">

                      <span className="is-active">{t('landing.preview.category1')}</span>

                      <span>{t('landing.preview.category2')}</span>

                    </div>

                    {['product1', 'product2'].map((key, i) => (

                      <div className="landing-qr-phone__item" key={key}>

                        <span>{i + 1}</span>

                        <div><strong>{t(`landing.preview.${key}`)}</strong><small>{i === 0 ? '89 MAD' : '72 MAD'}</small></div>

                      </div>

                    ))}

                  </div>

                </div>

                <div className="landing-qr-preview__footer">

                  {['noApp', 'fastSetup', 'multilingual'].map((key) => (

                    <span key={key}><Check size={13} />{t(`landing.hero.proof.${key}`)}</span>

                  ))}

                </div>

              </div>

            </div>

            <div className="landing-product__content" data-reveal>

              <span className="landing-section-kicker">{t('landing.product.eyebrow')}</span>

              <h2>{t('landing.product.title')}</h2>

              <p>{t('landing.product.description')}</p>

              <div className="landing-product__benefits">

                {['brand', 'updates', 'orders', 'analytics'].map((key) => (

                  <div key={key}>

                    <span><Check size={15} /></span>

                    <div>

                      <strong>{t(`landing.product.benefits.${key}.title`)}</strong>

                      <small>{t(`landing.product.benefits.${key}.description`)}</small>

                    </div>

                  </div>

                ))}

              </div>

              <Link to="/free-trial" className="landing-text-link">{t('landing.freeTrial.cta')}<ArrowRight size={16} /></Link>

            </div>

          </div>

        </section>

        {/* NEW: multilingual demo. Add keys landing.languages.{eyebrow,title,description} */}

        <section id="languages" className="landing-section landing-languages">

          <div className="landing-container landing-languages__grid">

            <div data-reveal>

              <span className="landing-section-kicker">{t('landing.languages.eyebrow')}</span>

              <h2>{t('landing.languages.title')}</h2>

              <p className="landing-languages__lead">{t('landing.languages.description')}</p>

              <div className="landing-languages__list" role="group" aria-label="Menu language">

                {DEMO_LANGS.map(([code, label], i) => (

                  <button key={code} type="button" aria-pressed={i === langIndex}

                    className={i === langIndex ? 'is-active' : ''} onClick={() => setLangIndex(i)}>

                    <b>{code.toUpperCase()}</b>{label}

                  </button>

                ))}

              </div>

            </div>

            <div className="landing-lang-phone" data-reveal>

              <div className="landing-lang-phone__screen" dir={lang[2]} key={lang[0]}>

                <div className="landing-lang-phone__head"><UtensilsCrossed size={16} />{t('landing.preview.restaurantName')}</div>

                <div className="landing-menu-preview__categories">

                  {lang[3].map((c, i) => <span key={c} className={i === 0 ? 'is-active' : ''}>{c}</span>)}

                </div>

                {lang[4].map((dish, i) => (

                  <div className="landing-lang-phone__item" key={dish} style={{ '--i': i }}>

                    <span>{i + 1}</span><strong>{dish}</strong><b>{[89, 18, 24][i]} MAD</b>

                  </div>

                ))}

                <div className="landing-lang-phone__cart"><span>{lang[5]}</span><b>107 MAD</b></div>

              </div>

            </div>

          </div>

        </section>

        {/* HOW */}

        <section id="how-it-works" className="landing-section landing-how">

          <div className="landing-container">

            <div className="landing-section-heading" data-reveal>

              <span>{t('landing.how.eyebrow')}</span>

              <h2>{t('landing.how.title')}</h2>

              <p>{t('landing.how.description')}</p>

            </div>

            <div className="landing-how__grid">

              {['create', 'customize', 'share', 'manage'].map((key, i) => (

                <article key={key} data-reveal style={{ '--d': `${i * 80}ms` }}>

                  <span>{String(i + 1).padStart(2, '0')}</span>

                  <h3>{t(`landing.how.steps.${key}.title`)}</h3>

                  <p>{t(`landing.how.steps.${key}.description`)}</p>

                </article>

              ))}

            </div>

          </div>

        </section>

        {/* CUSTOMER & WAITER EXPERIENCE */}
        <section id="customer-waiter-experience" className="landing-section landing-experience">
          <div className="landing-container">
            <div className="landing-section-heading" data-reveal>
              <span>{isRtl ? 'تجربة الاستخدام' : 'EXPÉRIENCE UTILISATEUR'}</span>
              <h2>{isRtl ? 'تجربة سلسة للزبون والنادل' : 'Une expérience simple, de la table au service'}</h2>
              <p>{isRtl
                ? 'اكتشف كيف تساعد القائمة الرقمية الزبائن على الطلب، وتساعد النوادل على تنظيم الخدمة.'
                : 'Découvrez comment le menu digital facilite la commande des clients et le travail de votre équipe en salle.'}</p>
            </div>

            <div className="landing-experience__grid">
              <article className="landing-experience__panel" data-reveal>
                <div className="landing-experience__header">
                  <span className="landing-experience__avatar"><Smartphone size={22} /></span>
                  <div>
                    <small>{isRtl ? 'الزبون' : 'CÔTÉ CLIENT'}</small>
                    <h3>{isRtl ? 'يطلب بسهولة من هاتفه' : 'Commander à son rythme'}</h3>
                  </div>
                </div>
                <p className="landing-experience__intro">{isRtl
                  ? 'تجربة واضحة دون الحاجة إلى تحميل تطبيق.'
                  : 'Une expérience claire, sans application à télécharger.'}</p>
                <div className="landing-experience__steps">
                  {[
                    { icon: QrCode, fr: 'Scanner le QR code', ar: 'مسح رمز QR', frDesc: 'Accéder au menu directement depuis la table.', arDesc: 'فتح القائمة مباشرة من الطاولة.' },
                    { icon: Smartphone, fr: 'Découvrir le menu', ar: 'تصفح القائمة', frDesc: 'Explorer les plats, descriptions et prix sur son téléphone.', arDesc: 'استعراض الأطباق والأوصاف والأسعار على الهاتف.' },
                    { icon: ShoppingBag, fr: 'Choisir et commander', ar: 'اختيار الطلب وإرساله', frDesc: 'Sélectionner ses produits et transmettre sa commande.', arDesc: 'اختيار المنتجات وإرسال الطلب.' },
                    { icon: ListChecks, fr: 'Retrouver ses commandes', ar: 'متابعة طلباته', frDesc: 'Consulter ses commandes et leurs détails.', arDesc: 'عرض طلباته وتفاصيلها.' },
                  ].map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div className="landing-experience__step" key={step.fr}>
                        <span className="landing-experience__step-icon"><Icon size={19} /></span>
                        <div>
                          <strong><span className="landing-experience__number">{String(index + 1).padStart(2, '0')}</span> {isRtl ? step.ar : step.fr}</strong>
                          <p>{isRtl ? step.arDesc : step.frDesc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>

              <article className="landing-experience__panel landing-experience__panel--waiter" data-reveal>
                <div className="landing-experience__header">
                  <span className="landing-experience__avatar"><ClipboardList size={22} /></span>
                  <div>
                    <small>{isRtl ? 'النادل' : 'CÔTÉ SERVEUR'}</small>
                    <h3>{isRtl ? 'خدمة أكثر تنظيماً' : 'Un service mieux organisé'}</h3>
                  </div>
                </div>
                <p className="landing-experience__intro">{isRtl
                  ? 'واجهة مخصصة لتسهيل إدارة الطلبات أثناء الخدمة.'
                  : 'Un espace dédié pour gérer les commandes pendant le service.'}</p>
                <div className="landing-experience__steps">
                  {[
                    { icon: ClipboardList, fr: 'Consulter les commandes', ar: 'عرض الطلبات', frDesc: 'Retrouver les commandes dans une vue centralisée.', arDesc: 'الاطلاع على الطلبات في واجهة موحدة.' },
                    { icon: ShoppingBag, fr: 'Créer une commande', ar: 'إنشاء طلب', frDesc: 'Saisir une commande pour un client si nécessaire.', arDesc: 'إدخال طلب نيابة عن الزبون عند الحاجة.' },
                    { icon: BellRing, fr: 'Rester informé', ar: 'البقاء على اطلاع', frDesc: 'Accéder aux informations de commande utiles au service.', arDesc: 'الوصول إلى معلومات الطلبات المفيدة للخدمة.' },
                    { icon: ListChecks, fr: 'Consulter les détails', ar: 'مراجعة التفاصيل', frDesc: 'Vérifier les articles et informations de chaque commande.', arDesc: 'التحقق من المنتجات وتفاصيل كل طلب.' },
                  ].map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div className="landing-experience__step" key={step.fr}>
                        <span className="landing-experience__step-icon"><Icon size={19} /></span>
                        <div>
                          <strong><span className="landing-experience__number">{String(index + 1).padStart(2, '0')}</span> {isRtl ? step.ar : step.fr}</strong>
                          <p>{isRtl ? step.arDesc : step.frDesc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* NEW: permanent QR. Add keys landing.permanent.{title,description,button,note,item} */}

        <section id="permanent-qr" className="landing-section landing-permanent">

          <div className="landing-container landing-permanent__grid">

            <div data-reveal>

              <h2>{t('landing.permanent.title')}</h2>

              <p>{t('landing.permanent.description')}</p>

              <div className="landing-permanent__demo">

                <div>

                  <small>{t('landing.permanent.item')}</small>

                  <strong className={pulse ? 'is-pulsing' : ''} aria-live="polite">{price} MAD</strong>

                </div>

                <button type="button" className="landing-button landing-button--small" onClick={updatePrice}>

                  <RefreshCw size={15} />{t('landing.permanent.button')}

                </button>

              </div>

              <small className="landing-permanent__note">{t('landing.permanent.note')}</small>

            </div>

            <div className="landing-permanent__tag" data-reveal aria-hidden="true">

              <QRCodeSVG value={DEMO_MENU_URL} size={190} bgColor="#ffffff" fgColor="#1f2937" level="M" />

              <strong>{t('landing.preview.restaurantName')}</strong>

            </div>

          </div>

        </section>

        {/* USE CASES */}

        <section id="use-cases" className="landing-section landing-use-cases">

          <div className="landing-container">

            <div className="landing-section-heading" data-reveal>

              <span>{t('landing.useCases.eyebrow')}</span>

              <h2>{t('landing.useCases.title')}</h2>

              <p>{t('landing.useCases.description')}</p>

            </div>

            <div className="landing-business-grid">

              {Object.entries(BUSINESS_ICONS).map(([key, Icon], i) => (

                <article key={key} tabIndex={0} data-reveal style={{ '--d': `${i * 50}ms` }}>

                  <span><Icon size={21} /></span>

                  <strong>{t(`landing.useCases.items.${key}.title`)}</strong>

                  <p>{t(`landing.useCases.items.${key}.description`)}</p>

                </article>

              ))}

            </div>

          </div>

        </section>

        {/* FREE TRIAL */}

        <section id="free-trial" className="landing-section landing-free-trial">

          <div className="landing-container landing-free-trial__grid">

            <div className="landing-free-trial__content" data-reveal>

              <span className="landing-section-kicker">{t('landing.freeTrial.eyebrow')}</span>

              <h2>{t('landing.freeTrial.title')}</h2>

              <p>{t('landing.freeTrial.description')}</p>

            </div>

            <div className="landing-free-trial__card" data-reveal>

              <span className="landing-free-trial__icon"><ClipboardCheck size={22} /></span>

              <div className="landing-free-trial__points">

                {['email', 'verify', 'business', 'review'].map((key) => (

                  <div key={key}>

                    <span><Check size={15} /></span>

                    <div>

                      <strong>{t(`landing.freeTrial.points.${key}.title`)}</strong>

                      <small>{t(`landing.freeTrial.points.${key}.description`)}</small>

                    </div>

                  </div>

                ))}

              </div>

              <Link to="/free-trial" className="landing-button landing-free-trial__button">

                {t('landing.freeTrial.cta')}<ArrowRight size={18} />

              </Link>

              <small className="landing-free-trial__note">{t('landing.freeTrial.note')}</small>

            </div>

          </div>

        </section>

        <section className="landing-final-cta">

          <div className="landing-container">

            <div className="landing-final-cta__card" data-reveal>

              <span><QrCode size={26} /></span>

              <div>

                <small>{t('landing.finalCta.eyebrow')}</small>

                <h2>{t('landing.finalCta.title')}</h2>

                <p>{t('landing.finalCta.description')}</p>

              </div>

              <Link to="/free-trial" className="landing-button landing-button--light">

                {t('landing.freeTrial.cta')}<ArrowRight size={18} />

              </Link>

            </div>

          </div>

        </section>

      </main>

    </div>

  );

}
